// packages/sdk/src/ops/auth.ts

import { request, response } from "../../proto/bundle";
import { getConnectionPool, checkAndThrow } from "../../core/connectionPool";
import { uuid } from "../../utils/uuid";
import { User } from "./user";
import { Organization } from "./orgnization";
import { checkMigrate, globalCache } from "../../init/sdkInit";
import { worker } from "../../index";
import { PinInListSDK } from "../dataOps/pins";
import { hashString } from "../../utils/hash";

export async function signInWithPassword({ email, password, totp, createTempPin }: {
    email: string;
    password: string;
    totp: string;
    createTempPin?: boolean
}): Promise<User | Organization> {
    if (!email || !password) {
        throw new Error("Email and password are required for sign-in");
    }
    if (!totp) {
        throw new Error("TOTP is required for sign-in");
    }
    const hashed_password = await hashString(password);
    const signIn = request.SignIn.create({ email, password: hashed_password, totp });
    const signInReq = request.Request.create({ id: null, reqCreateTime: Date.now(), signIn });
    const pool = await getConnectionPool();
    const signInResp = await pool.sendAndWait(signInReq);
    checkMigrate(signInResp.signIn.status)
    if (signInResp.signIn?.status !== 0) {
        throw new Error(signInResp.signIn?.message || "Sign-in failed");
    }
    if (!signInResp.signIn?.email) {
        throw new Error("No email in response for SignIn with password");
    }
    pool.setEmail(signInResp.signIn.email);
    if (createTempPin) {
        const pin = Math.floor(100000 + Math.random() * 900000).toString();
        const pin_name = "temp_session-" + Math.random() * 10000000000;
        const token = await sendAddPin(email, pin, pin_name);
        globalCache.set("created_pin_name", pin_name);
        pool.setPin(pin, token);
    }
    return await getUserOrOrg(signInResp.signIn.email ?? "");
}

export async function signInWithToken(token: Uint8Array, pin: string): Promise<User | Organization> {
    if (!token || !pin) {
        throw new Error("Token and pin are required for sign-in");
    }
    const hashed_pin = await hashString(pin);
    const req = request.Request.create({
        id: null,
        reqCreateTime: Date.now(),
        signIn: request.SignIn.create({
            email: "",
            pin: hashed_pin,
            data: token,
        }),
    });

    const pool = await getConnectionPool();
    const res = await pool.sendAndWait(req);
    checkMigrate(res.signIn.status)

    checkAndThrow(res);
    pool.setPin(pin, token);
    pool.setEmail(res.signIn?.email ?? "");
    worker.postMessage({ op: "signIn", token: token, pin: hashed_pin });

    return await getUserOrOrg(res.signIn?.email ?? "");
}

export async function listUserPins(): Promise<PinInListSDK[]> {
    const pins: PinInListSDK[] = [];
    let pageToken: Uint8Array | null = null;

    while (true) {
        const listPinsReq = request.ListPins.create({
            pageSize: 100,  // Use larger page size for efficiency
            pageToken: pageToken || undefined
        });
        const req = request.Request.create({ id: null, reqCreateTime: Date.now(), listPins: listPinsReq });

        const pool = await getConnectionPool();
        const res = await pool.sendAndWait(req);
        checkMigrate(res.listPins.status)

        if (res.listPins?.status !== 0) {
            throw new Error("Failed to list pins");
        }

        // Add pins from current page to result list
        for (const pin of res.listPins.pinObjects ?? []) {
            if (pin) {
                const pinObj = PinInListSDK.fromProto(pin);
                pins.push(pinObj);
            }
        }

        // Check if there are more pages
        pageToken = res.listPins.pageToken ?? null;
        if (!pageToken || pageToken.byteLength === 0) {
            break;
        }
    }
    // console.log("Total pins listed:", pins.length);
    let now = Date.now();
    for (const pin of pins) {
        let expiry_date = pin.getExpiryTime();
        const diff = expiry_date.getTime() - now;
        let name = pin.getName();
        let check = name.startsWith("temp_session-") || name.startsWith("session-") || name.length === 0;
        if (diff <= 0 && check) {
            deletePin(pin.getId())
        }
    }
    return pins;
}
export async function updatePinOps(pinName: string, ops: number[], uuids: Uint8Array[]): Promise<void> {
    if (!pinName) {
        throw new Error("Pin name and operations are required to update pin");
    }
    const updatePinReq = request.UpdatePinOps.create({ pinName: pinName, allowedOps: ops, allowedObjects: uuids });
    const req = request.Request.create({ id: null, reqCreateTime: Date.now(), updatePinOps: updatePinReq });

    const pool = await getConnectionPool();
    const res = await pool.sendAndWait(req);
    checkMigrate(res.updatePinOps.status)

    if (res.updatePinOps?.status !== 0) {
        throw new Error(res.updatePinOps?.message || "Failed to update pin operations");
    }
}
export async function sendAddPin(email: string, pin: string, pin_name?: string | null): Promise<Uint8Array> {

    const win = window;
    const nav = win.navigator;

    // User Agent
    // const userAgent = nav.userAgent || "unknown";

    // Platform
    const platform = nav.platform || "unknown";

    // Language
    const language = nav.language || "English";

    // Timezone
    const dtf = new Intl.DateTimeFormat();
    const timeZone = dtf.resolvedOptions().timeZone || "unknown";

    // Combined client string
    const client = `${platform}:${language}:${timeZone}`;
    // console.log("Client String: ", client);




    if (!email || !pin) {
        throw new Error("Email and pin are required to add pin");
    }
    const hashed_pin = await hashString(pin);
    const addPinReq = request.AddPin.create({ email, pin: hashed_pin, name: pin_name ?? null, client });
    const req = request.Request.create({ id: null, reqCreateTime: Date.now(), addPin: addPinReq });

    const pool = await getConnectionPool();
    const res = await pool.sendAndWait(req);
    checkMigrate(res.addPin.status)

    if (res.addPin?.status !== 0 || !res.addPin?.data) {
        throw new Error(res.addPin?.message || "Failed to add pin");
    }
    globalCache.set(pin_name ?? "Null", res.addPin.idToClient)
    pool.setPin(pin, res.addPin.data);
    worker.postMessage({ op: "pinAdded", pin: hashed_pin, token: res.addPin.data });
    return res.addPin.data;
}

export async function sendAddPinWithOps(pin: string, ops: number[], uuids: Uint8Array[], pin_name?: string | null, ttl?: number): Promise<Uint8Array> {

    const win = window;
    const nav = win.navigator;
    console.log(ttl)

    // User Agent
    // const userAgent = nav.userAgent || "unknown";

    // Platform
    const platform = nav.platform || "unknown";

    // Language
    const language = nav.language || "English";

    // Timezone
    const dtf = new Intl.DateTimeFormat();
    const timeZone = dtf.resolvedOptions().timeZone || "unknown";

    // Combined client string
    const client = `${platform}:${language}:${timeZone}`;




    if (!pin) {
        throw new Error("Email and pin are required to add pin");
    }
    // const uuids_bytes: Uint8Array[] = uuids_hexa.map(hex => Uint8Array.from(Buffer.from(hex, "hex")));

    const pool = await getConnectionPool();
    const email = pool.getEmail();
    const hashed_pin = await hashString(pin);
    const addPinReq = request.AddPin.create({ email, pin: hashed_pin, allowedOps: ops, name: pin_name ?? null, allowedObjects: uuids, client, ttl: ttl ?? null });
    console.log(addPinReq)
    const req = request.Request.create({ id: null, reqCreateTime: Date.now(), addPin: addPinReq });
    const res = await pool.sendAndWait(req);
    checkMigrate(res.addPin.status)

    if (res.addPin?.status !== 0 || !res.addPin?.data) {
        throw new Error(res.addPin?.message || "Failed to add pin");
    }
    // pool.setPin(pin, res.addPin.data);
    return res.addPin.data;
}
export async function deletePin(id: Uint8Array): Promise<void> {
    if (!id || id.byteLength === 0) {
        throw new Error("ID is required to delete pin");
    }
    const deletePinReq = request.DeletePin.create({ idSentToClient: id });
    const req = request.Request.create({ id: null, reqCreateTime: Date.now(), deletePin: deletePinReq });

    const pool = await getConnectionPool();
    const res = await pool.sendAndWait(req);
    checkMigrate(res.deletePin.status)

    if (res.deletePin?.status !== 0) {
        throw new Error(res.deletePin?.message || "Failed to delete pin");
    }
}

export async function getUserOrOrg(email: string, skipCache?: boolean): Promise<User | Organization> {

    if (!email) {
        throw new Error("Email is required to get user or organization");
    }
    if (!skipCache && globalCache.has(email + "UserDetails")) {
        const cached = globalCache.get(email + "UserDetails");
        if (cached instanceof User || cached instanceof Organization) {
            return cached;
        }
    }
    const getUserReq = request.GetUser.create({ email });
    const req = request.Request.create({ id: null, reqCreateTime: Date.now(), getUser: getUserReq });
    const pool = await getConnectionPool();
    const res = await pool.sendAndWait(req);
    checkMigrate(res.getUser.status)
    const user = res.getUser?.user;

    if (!user) throw new Error("No user data in response");

    if (user.userObj) {
        const u = user.userObj;
        const userType = (u.organization ?? "").length === 0 ? "User" : "Employee";
        let operations: number[] = [];
        if (userType === "Employee") {
            operations = u.operations;
        }
        // console.log("OPerations", operations);
        const chacheObj = new User(email, "", operations, u.firstname ?? "", u.lastname ?? "", userType, u.organization ?? "", u.phone ?? "", u.address ?? "", u.plan ?? 0, u.location ?? "", u.profilePicture ?? undefined, u.planExpiryDate ?? undefined);
        globalCache.set(email + "UserDetails", chacheObj);
        return chacheObj;
    }

    if (user.organizationObj) {
        const o = user.organizationObj;

        const chacheObj = new Organization(email, o.name ?? "", "", o.phone ?? "", o.address ?? "", o.taxId ?? "", o.vatId ?? "", o.registrationId ?? "", o.organizationId ?? new Uint8Array(), o.plan, o.logo ?? new Uint8Array(), o.location ?? "", o.planExpiryDate ?? undefined);
        globalCache.set(email + "UserDetails", chacheObj);
        return chacheObj;
    }

    throw new Error("Unrecognized user structure");
}

export async function loadTokenFromFile(filePath?: string): Promise<Uint8Array> {
    // if (typeof process === "undefined" || !process.versions?.node) {
    const token = window.localStorage.getItem("izauthtoken");
    if (!token) {
        throw new Error("Token not found in localStorage or sessionStorage");
    }
    return Uint8Array.from(atob(token), c => c.charCodeAt(0));
    // }
    // const fs = await import("fs");
    // const os = await import("os");
    // const path = await import("path");

    // const resolvedPath = filePath || path.join(os.homedir(), ".iz-token");

    // if (!fs.existsSync(resolvedPath)) {
    //     throw new Error(`Token file not found: ${resolvedPath}`);
    // }

    // return await fs.promises.readFile(resolvedPath); // returns Buffer, works as Uint8Array
}

export async function createTokenSignInRequest(pin: string, token: Uint8Array): Promise<request.Request> {
    let hashedPin = await hashString(pin);
    return request.Request.create({
        id: uuid(),
        reqCreateTime: Date.now(),
        signIn: {
            pin: hashedPin,
            data: token,
        }
    });
}

export function createSignInRequestForExternalUser(signInRequest: request.SignInToSpace) {
    let req = request.Request.create({
        signInToSpace: signInRequest,
        reqCreateTime: Date.now(),
        id: uuid()
    });
    return req;
}

